import catalog from './models.json';

export interface WanField {
  type: string; default?: string | number | boolean; enum?: (string | number)[];
  minimum?: number; maximum?: number; maxItems?: number; minItems?: number;
}
export interface WanModel {
  id: string; key: string; version: string; operation: string;
  mediaType: 'image' | 'video'; basePrice: number; required: string[];
  fields: Record<string, WanField>;
}
/** Audited against WaveSpeed public API schemas, 2026-11-03. Prices in micro USD. */
export const wanModels = catalog as WanModel[];
export const wanVersions = ['2.5', '2.6', '2.7', '3.0', '3.0-prime'];
export const getWanModel = (key: string) => wanModels.find(model => model.key === key);
export type WanInput = Record<string, any>;
export const isMediaField = (name: string) => /^(image|images|last_image|video|audio|reference_images|reference_videos|reference_audios|reference_files)$/.test(name);

export function wanDefaults(model: WanModel): WanInput {
  return Object.fromEntries(Object.entries(model.fields)
    .filter(([, field]) => field.default !== undefined)
    .map(([name, field]) => [name, field.default]));
}

/** List prices × 700 credits/USD. Source seconds must be measured by the server. */
export function wanCredits(model: WanModel, input: WanInput, sourceSeconds = 0): number {
  if (model.mediaType === 'image') return Math.ceil(model.basePrice * 700 / 1e6);
  const p = { ...wanDefaults(model), ...Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)) };
  if (model.version === '2.2') {
    const sourceBilled = ['speech-to-video', 'video-edit', 'animate'].includes(model.operation);
    const seconds = sourceBilled ? Math.max(3, Math.ceil(sourceSeconds)) : Number(p.duration);
    const multiplier = sourceBilled && p.resolution === '720p' ? 2 : 1;
    return Math.ceil(model.basePrice * seconds * multiplier * 700 / 1e6);
  }
  let seconds = Number(p.duration);
  let price = model.basePrice;
  const resolution = p.resolution || (['1920*1080', '1080*1920'].includes(p.size) ? '1080p'
    : ['832*480', '480*832'].includes(p.size) ? '480p' : '720p');
  let multiplier = resolution === '1080p' ? 1.5 : resolution === '480p' ? 0.5 : 1;
  if (model.operation === 'image-to-video-pro') {
    price = resolution === '4k' ? 180000 : resolution === '2k' ? 160000 : 140000;
    multiplier = 1;
  }
  if (model.version.startsWith('3.0')) {
    multiplier = resolution === '1080p' ? 2 : resolution === '480p' ? 0.5 : 1;
    if (model.operation === 'reference-to-video') price /= 3;
    if (['reference-to-video', 'video-edit'].includes(model.operation)) seconds += Math.ceil(sourceSeconds);
  } else if (model.operation.startsWith('reference-to-video')) seconds += 5;
  if (model.operation.endsWith('flash') && p.generate_audio === false) multiplier *= 0.5;
  return Math.ceil(Math.round(price * seconds * multiplier * 700) / 1e6);
}

/** Lowest supported configuration, including mandatory source-video billing. */
export function wanMinimumCredits(model: WanModel): number {
  if (model.mediaType === 'image') return wanCredits(model, {});
  let inputs = [wanDefaults(model)];
  for (const name of ['duration', 'resolution', 'size', 'generate_audio']) {
    const field = model.fields[name];
    if (!field) continue;
    const choices = field.enum || (field.type === 'boolean' ? [false, true]
      : name === 'duration' ? [field.minimum ?? field.default] : undefined);
    if (choices) inputs = inputs.flatMap(input => choices.map(value => ({ ...input, [name]: value })));
  }
  // Reference generation can use images alone; video editing requires at least 1s of source video.
  const sourceSeconds = model.version.startsWith('3.0') && model.operation === 'video-edit' ? 1 : 0;
  return Math.min(...inputs.map(input => wanCredits(model, input, sourceSeconds)));
}
